/*
 * Copyright © 2026 Medaica, Inc
 *
 * All rights reserved.
 *
 * This code is confidential and proprietary information belonging to Medaica, Inc.
 * Unauthorized copying, distribution, or use of this code, in whole or in part,
 * is strictly prohibited, and may constitute a violation of intellectual property rights.
 *
 * If you have received this code in error, please notify the owner immediately
 * at support@medaica.com and delete this file from your system.
 */

/* eslint-disable @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-explicit-any --
 * The report document and pcg-report core are loose-typed by design: the
 * core is vendored prototype JS with a hand-written surface .d.ts. */

import React, { ReactElement } from "react"
import {
  INTERNAL_RECORDING_NOTES_INSTRUCTION,
  recordingNoteObservationText,
  recordingNoteReportNote,
  updateRecordingNoteReportNote,
} from "./core"

/**
 * Reviewer observations of the Medaica Report — React rebuild of the
 * prototype's renderRecordingNotes (Medaica Report v0.6.1 at
 * cardiac-panel-v4 git eba1cd1). A note from the viewer has two parts: its
 * structured observations and internal note, which are HCP review context
 * only, and its Report note, the only part the report shows and prints.
 *
 * - "Internal Viewer observations": edit mode only, read-only, with the
 *   upstream instruction — every note with observation content.
 * - "Report notes": every note with a Report note, in every mode; the wording
 *   is editable in edit mode and persists as `reportNote` (with
 *   `reportEditedAt`).
 *
 * Deviations from the prototype: the Report note is edited via a textarea
 * rather than contenteditable.
 *
 * Edited text enters the note on every keystroke. With `onDraftInput` the
 * owner is told without a re-render (which would reset the textarea being
 * typed in); without it, every keystroke goes through `onChanged`.
 */

const noteLabel = (note: any, index: number): string =>
  `Recording ${String(note.recordingSequence || index + 1)} · ${String(
    note.position || `Recording ${note.recordingSequence || index + 1}`
  )}`

const HeartReportRecordingNotes = ({
  notes,
  editMode,
  revision,
  onChanged,
  onDraftInput,
}: {
  notes: any[]
  editMode?: boolean
  revision?: number
  onChanged?: () => void
  /** A keystroke in the wording editor: unsaved text exists, nothing re-rendered. */
  onDraftInput?: () => void
}): ReactElement | null => {
  const indexed = notes.map((note: any, index: number) => ({ note, index })).filter(({ note }) => note)
  const internalNotes = editMode ? indexed.filter(({ note }) => recordingNoteObservationText(note)) : []
  const reportNotes = indexed.filter(({ note }) => recordingNoteReportNote(note))
  if (internalNotes.length === 0 && reportNotes.length === 0) {
    return null
  }

  return (
    <>
      {internalNotes.length > 0 && (
        <section
          className="recording-notes-review recording-notes-internal"
          aria-label="Internal Viewer observations"
          data-testid="recording-notes-internal"
        >
          <div className="recording-notes-review-title">Internal Viewer observations</div>
          <p className="recording-notes-review-instruction">{INTERNAL_RECORDING_NOTES_INSTRUCTION}</p>
          {internalNotes.map(({ note, index }, position) => (
            <div className="recording-note-source" key={`internal:${index}`}>
              <div className="recording-note-source-label">{noteLabel(note, position)}</div>
              <div className="recording-note-source-text">{recordingNoteObservationText(note)}</div>
            </div>
          ))}
        </section>
      )}
      {reportNotes.length > 0 && (
        <section className="recording-notes-review" aria-label="Report notes" data-testid="recording-notes-review">
          <div className="recording-notes-review-title">Report notes</div>
          {reportNotes.map(({ note, index }, position) => {
            const label = noteLabel(note, position)
            return (
              <div className="recording-note-source" key={`report:${index}`}>
                <div className="recording-note-source-label">{label}</div>
                {editMode ? (
                  <textarea
                    key={`report-note:${revision ?? 0}`}
                    className="recording-note-report-text"
                    aria-label={`Edit Report note for ${label}`}
                    defaultValue={recordingNoteReportNote(note)}
                    onChange={(event) => {
                      updateRecordingNoteReportNote(note, event.target.value)
                      ;(onDraftInput ?? onChanged ?? (() => undefined))()
                    }}
                  />
                ) : (
                  <div className="recording-note-source-text">{recordingNoteReportNote(note)}</div>
                )}
              </div>
            )
          })}
        </section>
      )}
    </>
  )
}

export default HeartReportRecordingNotes
